// src/pages/ScanUSASalesPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { apiUrl } from "../config/api";

type USAOrderItem = {
  id: number;
  bill_no: string;
  product_code: string | null;
  product_name: string | null;
  qty: number;
  usaPrice: number | null;
  unit_cost: number;
  usa_qty_sold: number;
};

type USAOrder = {
  bill_no: string;
  usa_status: string;
  created_at: string | null;
  order_items: USAOrderItem[];
};

type AllocationSource = {
  order_item_id: number;
  bill_no: string;
  product_code: string;
  product_name: string;
  created_at: string | null;
  remaining: number;
  usaPrice: number | null;
  unit_cost: number;
};

type CartLine = {
  product_code: string;
  product_name: string;
  qty: number;
  usd: string;
};

type AllocationPreview = {
  order_item_id: number;
  bill_no: string;
  qty: number;
  unit_cost: number;
  usd: number;
};

const TOKEN_STORAGE_KEY = "bg_shop_token";

function toNumber(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toUSAOrderItem(row: unknown): USAOrderItem | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const id = Number(d.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    bill_no: typeof d.bill_no === "string" ? d.bill_no.trim() : "",
    product_code: typeof d.product_code === "string" ? d.product_code.trim().toUpperCase() : null,
    product_name: typeof d.product_name === "string" ? d.product_name : null,
    qty: toNumber(d.qty),
    usaPrice: d.usa_price === null || d.usa_price === undefined ? null : toNumber(d.usa_price),
    unit_cost: toNumber(d.unit_cost),
    usa_qty_sold: toNumber(d.usa_qty_sold),
  };
}

function toUSAOrder(row: unknown): USAOrder | null {
  if (!row || typeof row !== "object") return null;
  const d = row as Record<string, unknown>;
  const bill_no = typeof d.bill_no === "string" ? d.bill_no.trim() : "";
  if (!bill_no) return null;
  return {
    bill_no,
    usa_status:
      typeof d.usa_status === "string" && d.usa_status.trim()
        ? d.usa_status.trim().toUpperCase()
        : "PENDING",
    created_at: typeof d.created_at === "string" ? d.created_at : null,
    order_items: Array.isArray(d.order_items)
      ? d.order_items.map(toUSAOrderItem).filter((x): x is USAOrderItem => x != null)
      : [],
  };
}

function formatCurrency(value: number, currency: "THB" | "USD" = "THB"): string {
  const locale = currency === "USD" ? "en-US" : "th-TH";
  return value.toLocaleString(locale, { style: "currency", currency });
}

function formatUSAPrice(value: number | null): string {
  return value == null ? "ยังไม่ตั้งราคา" : formatCurrency(value, "USD");
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function sortOldestFirst(a: AllocationSource, b: AllocationSource): number {
  const at = a.created_at ? new Date(a.created_at).getTime() : 0;
  const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
  if (at !== bt) return at - bt;
  if (a.bill_no !== b.bill_no) return a.bill_no.localeCompare(b.bill_no);
  return a.order_item_id - b.order_item_id;
}

function toCanonicalProductCode(rawCode: string): string {
  const input = rawCode.trim();
  if (!input) return "";
  return (/^\d+$/.test(input) ? `PD${input}` : input).toUpperCase();
}

function splitUsd(totalUsd: number, allocations: Omit<AllocationPreview, "usd">[], totalQty: number): AllocationPreview[] {
  const cents = Math.round(totalUsd * 100);
  let usedCents = 0;
  return allocations.map((alloc, idx) => {
    const usdCents =
      idx === allocations.length - 1
        ? cents - usedCents
        : Math.round((cents * alloc.qty) / totalQty);
    usedCents += usdCents;
    return { ...alloc, usd: usdCents / 100 };
  });
}

export default function ScanUSASalesPage(): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanLoopRef = useRef<number | null>(null);
  const gotOneRef = useRef<boolean>(false);
  const manualInputRef = useRef<HTMLInputElement | null>(null);

  const [reader] = useState(() => {
    const r = new BrowserMultiFormatReader();
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
    ]);
    r.setHints?.(hints);
    return r;
  });

  const [orders, setOrders] = useState<USAOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [resultText, setResultText] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [cart, setCart] = useState<CartLine[]>([]);
  const [note, setNote] = useState("");

  async function loadOrders(): Promise<void> {
    setLoading(true);
    setErrorMsg("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/usa-shop-orders"), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok || !Array.isArray(data.items)) {
        throw new Error(data?.error ?? "failed_to_fetch_usa_shop_orders");
      }
      setOrders(data.items.map(toUSAOrder).filter((x: USAOrder | null): x is USAOrder => x != null));
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "โหลดบิล USA ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadOrders();
  }, []);

  const allocationSources = useMemo(() => {
    const rows: AllocationSource[] = [];
    for (const order of orders) {
      if (order.usa_status !== "PENDING") continue;
      for (const item of order.order_items) {
        const code = item.product_code?.trim().toUpperCase();
        const remaining = item.qty - item.usa_qty_sold;
        if (!code || remaining <= 0) continue;
        rows.push({
          order_item_id: item.id,
          bill_no: order.bill_no,
          product_code: code,
          product_name: item.product_name ?? "",
          created_at: order.created_at,
          remaining,
          usaPrice: item.usaPrice,
          unit_cost: item.unit_cost,
        });
      }
    }
    return rows.sort(sortOldestFirst);
  }, [orders]);

  const remainingByCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of allocationSources) {
      map.set(row.product_code, (map.get(row.product_code) ?? 0) + row.remaining);
    }
    return map;
  }, [allocationSources]);

  const productNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of allocationSources) {
      if (!map.has(row.product_code)) map.set(row.product_code, row.product_name);
    }
    return map;
  }, [allocationSources]);

  // PMN-CRITICAL(/ScanUSASalesPage): usaPrice comes from products.usa_price like /USAStockPage and is display-only; save payload still uses manually entered line.usd.
  const usaPriceByCode = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const row of allocationSources) {
      if (!map.has(row.product_code)) map.set(row.product_code, row.usaPrice);
    }
    return map;
  }, [allocationSources]);

  // PMN-CRITICAL(/ScanUSASalesPage): cartHasOverLimit blocks the save button + drives inline warning per row.
  const cartHasOverLimit = useMemo(() => {
    return cart.some((line) => line.qty > (remainingByCode.get(line.product_code) ?? 0));
  }, [cart, remainingByCode]);

  function playBeep() {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = 880;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0.2, ctx.currentTime);
      o.start();
      o.stop(ctx.currentTime + 0.12);
      setTimeout(() => ctx.close(), 300);
    } catch {}
  }

  // PMN-CRITICAL(/ScanUSASalesPage): scanner/manual entry both feed addProductByCode; digit-only shorthand must match /ScanToUSAPage.
  async function addProductByCode(rawCode: string): Promise<void> {
    const expanded = toCanonicalProductCode(rawCode);
    if (!expanded) return;
    setResultText(expanded);
    setSuccessMsg("");
    setErrorMsg("");
    playBeep();

    const available = remainingByCode.get(expanded) ?? 0;
    if (available <= 0) {
      setErrorMsg(`ไม่พบสินค้า USA ที่ยังขายได้: ${expanded}`);
      return;
    }

    let nextQty = 1;
    let blocked = false;
    setCart((prev) => {
      const idx = prev.findIndex((line) => line.product_code === expanded);
      const currentQty = idx >= 0 ? prev[idx].qty : 0;
      if (currentQty + 1 > available) {
        blocked = true;
        nextQty = currentQty;
        return prev;
      }
      if (idx >= 0) {
        const copy = [...prev];
        nextQty = copy[idx].qty + 1;
        copy[idx] = { ...copy[idx], qty: nextQty };
        return copy;
      }
      const product_name = productNameByCode.get(expanded) ?? "";
      return [{ product_code: expanded, product_name, qty: 1, usd: "" }, ...prev];
    });

    if (blocked) {
      setErrorMsg(`จำนวน ${expanded} เกิน remaining รวม (${available})`);
      return;
    }
    setSuccessMsg(`เพิ่ม ${expanded} x${nextQty}`);
  }

  const stopScanLoop = () => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    scanLoopRef.current = null;
    gotOneRef.current = false;
  };

  const stopStream = () => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const runDecodeLoop = () => {
    const video = videoRef.current;
    if (!video) return;
    const tick = async () => {
      if (gotOneRef.current) return;
      try {
        const result = await reader.decodeOnceFromVideoElement(video).catch(() => null);
        if (result && !gotOneRef.current) {
          gotOneRef.current = true;
          const code = result.getText();
          stopScanLoop();
          stopStream();
          setScanning(false);
          await addProductByCode(code);
          return;
        }
      } catch {}
      scanLoopRef.current = requestAnimationFrame(tick);
    };
    scanLoopRef.current = requestAnimationFrame(tick);
  };

  // PMN-CRITICAL(/ScanUSASalesPage): after first decode, stop loop + stream or the same barcode can be added repeatedly.
  async function startScan(): Promise<void> {
    setErrorMsg("");
    setResultText("");
    gotOneRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setScanning(true);
      runDecodeLoop();
    } catch (err: any) {
      setErrorMsg(err?.message ?? "เปิดกล้องไม่ได้ (ต้อง https หรือ localhost)");
      setScanning(false);
    }
  }

  function stopScan() {
    stopScanLoop();
    stopStream();
    setScanning(false);
  }

  useEffect(() => {
    return () => {
      stopScanLoop();
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildAllocations(productCode: string, qty: number, usdRaw: string): AllocationPreview[] {
    let remaining = qty;
    const base: Omit<AllocationPreview, "usd">[] = [];
    for (const source of allocationSources.filter((row) => row.product_code === productCode)) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, source.remaining);
      base.push({
        order_item_id: source.order_item_id,
        bill_no: source.bill_no,
        qty: take,
        unit_cost: source.unit_cost,
      });
      remaining -= take;
    }
    if (remaining > 0) return [];
    const usd = Number(usdRaw || 0);
    return splitUsd(Number.isFinite(usd) ? usd : 0, base, qty);
  }

  // PMN-NOTE(/ScanUSASalesPage): allow any qty >= 0 — saveCart skips qty=0 lines; inline warning + disabled save button handle qty > available.
  function setQty(productCode: string, rawQty: number) {
    const qty = Math.max(0, Math.floor(Number.isFinite(rawQty) ? rawQty : 0));
    setCart((prev) => prev.map((line) => (line.product_code === productCode ? { ...line, qty } : line)));
  }

  function setUsd(productCode: string, usd: string) {
    if (usd !== "" && !/^\d*\.?\d{0,2}$/.test(usd)) return;
    setCart((prev) => prev.map((line) => (line.product_code === productCode ? { ...line, usd } : line)));
  }

  function removeLine(productCode: string) {
    setCart((prev) => prev.filter((line) => line.product_code !== productCode));
  }

  // PMN-CRITICAL(/ScanUSASalesPage): allocation is oldest PENDING USA bill first; batch save must be atomic so split records cannot partially insert.
  async function saveCart(): Promise<void> {
    if (cart.length === 0) {
      setErrorMsg("ยังไม่มีสินค้าในตะกร้า");
      return;
    }

    const records: Array<{ order_item_id: number; qty: number; usd: number; note: string | null }> = [];
    for (const line of cart) {
      // PMN-CRITICAL(/ScanUSASalesPage): qty=0 lines are skipped silently (treat as "not selling this row").
      if (line.qty === 0) continue;
      if (line.usd.trim() === "") {
        setErrorMsg(`กรอก USD ของ ${line.product_code} ก่อนบันทึก`);
        return;
      }
      const usd = Number(line.usd);
      if (!Number.isFinite(usd) || usd < 0) {
        setErrorMsg(`USD ของ ${line.product_code} ต้องไม่ติดลบ`);
        return;
      }
      const available = remainingByCode.get(line.product_code) ?? 0;
      if (line.qty > available) {
        setErrorMsg(`${line.product_code} เหลือขายได้ ${available} แต่ในตะกร้า ${line.qty}`);
        return;
      }
      const allocations = buildAllocations(line.product_code, line.qty, line.usd);
      if (allocations.length === 0) {
        setErrorMsg(`จัด allocation ของ ${line.product_code} ไม่สำเร็จ`);
        return;
      }
      for (const alloc of allocations) {
        records.push({
          order_item_id: alloc.order_item_id,
          qty: alloc.qty,
          usd: alloc.usd,
          note: note.trim() || null,
        });
      }
    }

    if (records.length === 0) {
      setErrorMsg("ทุกแถวมี Qty=0 — ไม่มีอะไรให้บันทึก");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
      const resp = await fetch(apiUrl("/orders/usa-sale-records/batch"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ records, sold_at: new Date().toISOString() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        if (data?.error === "qty_exceeds_remaining") {
          throw new Error(`จำนวนเกิน remaining ของ order_item ${data?.meta?.order_item_id ?? ""}`);
        }
        if (data?.error === "usa_order_not_pending") {
          throw new Error(`บิล ${data?.meta?.bill_no ?? ""} ไม่ใช่ PENDING แล้ว`);
        }
        throw new Error(typeof data?.error === "string" ? data.error : "failed_to_save_cart");
      }
      setSuccessMsg(`บันทึกการขาย ${data.total_records ?? records.length} records เรียบร้อย`);
      setCart([]);
      setNote("");
      await loadOrders();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "บันทึกตะกร้าไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  }

  const totalQty = cart.reduce((sum, line) => sum + line.qty, 0);
  const totalUsd = cart.reduce((sum, line) => sum + Number(line.usd || 0), 0);

  return (
    <div className="min-vh-100 bg-body-tertiary">
      <div className="container py-4 py-md-5">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1 text-danger">🇺🇸 สแกนบันทึกขาย USA</h4>
            <div className="small text-muted">บันทึก USD-only แล้วค่อยใส่ rate/THB ภายหลัง</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" disabled={loading} onClick={() => void loadOrders()}>
              รีเฟรชบิล
            </button>
            <a href="/USASalesPayoutPage" className="btn btn-outline-primary btn-sm">
              ดูยอดขาย USA
            </a>
            <a href="/" className="btn btn-link text-decoration-none">
              กลับเมนู
            </a>
          </div>
        </div>

        {errorMsg && <div className="alert alert-warning small">{errorMsg}</div>}
        {successMsg && <div className="alert alert-success small">{successMsg}</div>}

        <div className="row g-3">
          <div className="col-12 col-lg-5">
            <div className="card border-0 shadow-sm rounded-4 mb-3">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold">Scanner</div>
                  <span className="badge text-bg-secondary">{loading ? "กำลังโหลดบิล" : `${allocationSources.length} lots พร้อมขาย`}</span>
                </div>
                <div className="ratio ratio-4x3 bg-dark rounded-3 overflow-hidden mb-3">
                  <video ref={videoRef} className="w-100 h-100" style={{ objectFit: "cover" }} muted playsInline />
                </div>
                <div className="d-grid gap-2">
                  {!scanning ? (
                    <button className="btn btn-danger" disabled={loading} onClick={() => void startScan()}>
                      เปิดกล้องสแกน
                    </button>
                  ) : (
                    <button className="btn btn-outline-danger" onClick={stopScan}>
                      หยุดสแกน
                    </button>
                  )}
                </div>
                <form
                  className="mt-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const code = manualCode;
                    setManualCode("");
                    void addProductByCode(code);
                  }}
                >
                  <label className="form-label small text-muted">พิมพ์รหัสสินค้า</label>
                  <div className="input-group">
                    <input
                      ref={manualInputRef}
                      className="form-control"
                      placeholder="PD1083 / pd1083 / 1083"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                    />
                    <button className="btn btn-primary" type="submit" disabled={loading || manualCode.trim() === ""}>
                      เพิ่ม
                    </button>
                  </div>
                  <div className="form-text">
                    ระบบจะแปลงเป็นรหัสหลัก เช่น <strong>pd1083</strong> หรือ <strong>1083</strong> → <strong>PD1083</strong>
                  </div>
                </form>
                {resultText && <div className="small text-muted mt-2">ล่าสุด: <strong>{resultText}</strong></div>}
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3">
                <label className="form-label small text-muted">หมายเหตุของรอบนี้</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="เช่น ลูกค้า / ช่องทาง / order ref"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-7">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <div>
                    <div className="fw-semibold">ตะกร้าบันทึกขาย</div>
                    <div className="small text-muted">
                      รวม {totalQty} ชิ้น / {formatCurrency(totalUsd, "USD")}
                    </div>
                  </div>
                  <button
                    className="btn btn-success"
                    disabled={saving || cart.length === 0 || cartHasOverLimit}
                    title={cartHasOverLimit ? "มีบางแถวที่ Qty เกินคงเหลือ" : undefined}
                    onClick={() => void saveCart()}
                  >
                    {saving ? "กำลังบันทึก..." : cartHasOverLimit ? "⚠ แก้ Qty ที่เกินก่อน" : "บันทึกตะกร้า"}
                  </button>
                </div>

                {cart.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    {loading ? "กำลังโหลดบิล USA..." : "สแกนหรือพิมพ์รหัสสินค้าเพื่อเพิ่มลงตะกร้า"}
                  </div>
                ) : (
                  <div className="vstack gap-3">
                    {cart.map((line) => {
                      const available = remainingByCode.get(line.product_code) ?? 0;
                      const allocations = buildAllocations(line.product_code, line.qty, line.usd);
                      const usaPrice = usaPriceByCode.get(line.product_code) ?? null;
                      const referenceUsd = usaPrice == null ? null : usaPrice * line.qty;
                      return (
                        <div key={line.product_code} className="border rounded-3 p-3 bg-body">
                          <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                            <div>
                              <div className="fw-semibold">{line.product_code}</div>
                              <div className="small text-muted">{line.product_name}</div>
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                <span className={`badge rounded-pill border ${usaPrice == null ? "text-bg-warning" : "text-bg-light text-body"}`}>
                                  ราคา USA/ชิ้น {formatUSAPrice(usaPrice)}
                                </span>
                                <span className="badge rounded-pill text-bg-light border text-body">
                                  ยอดตามราคา USA {referenceUsd == null ? "-" : formatCurrency(referenceUsd, "USD")}
                                </span>
                              </div>
                            </div>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => removeLine(line.product_code)}>
                              ลบ
                            </button>
                          </div>

                          {(() => {
                            const isOverLimit = line.qty > available;
                            const isZero = line.qty === 0;
                            return (
                              <div className="row g-2 mb-2">
                                <div className="col-6 col-md-3">
                                  <label className="form-label small text-muted">Qty</label>
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    className={`form-control ${isOverLimit ? "is-invalid" : ""} ${isZero ? "text-warning" : ""}`}
                                    value={line.qty}
                                    onChange={(e) => setQty(line.product_code, Number(e.target.value))}
                                  />
                                  {isOverLimit && (
                                    <div className="invalid-feedback d-block small mt-1">
                                      ⚠ เกินจำนวนคงเหลือ (สูงสุด <strong>{available}</strong> ชิ้น)
                                    </div>
                                  )}
                                  {!isOverLimit && isZero && (
                                    <div className="small text-warning mt-1">
                                      ℹ️ Qty=0 — แถวนี้จะถูกข้ามตอนบันทึก
                                    </div>
                                  )}
                                </div>
                                <div className="col-6 col-md-3">
                                  <label className="form-label small text-muted">คงเหลือใน USA</label>
                                  <div
                                    className="form-control bg-body-secondary text-body-emphasis fw-semibold"
                                    style={{ pointerEvents: "none", userSelect: "none", cursor: "not-allowed" }}
                                    tabIndex={-1}
                                    aria-readonly
                                  >
                                    {available}
                                  </div>
                                </div>
                                <div className="col-6 col-md-3">
                                  <label className="form-label small text-muted">ราคา USA อ้างอิง</label>
                                  <div
                                    className={`form-control bg-body-secondary ${usaPrice == null ? "text-warning" : "text-body-emphasis"}`}
                                    style={{ pointerEvents: "none", userSelect: "none", cursor: "not-allowed" }}
                                    tabIndex={-1}
                                    aria-readonly
                                  >
                                    <span className="fw-semibold">{formatUSAPrice(usaPrice)}</span>
                                  </div>
                                  <div className="small text-muted mt-1">
                                    รวมตาม Qty: {referenceUsd == null ? "-" : formatCurrency(referenceUsd, "USD")}
                                  </div>
                                </div>
                                <div className="col-6 col-md-3">
                                  <label className="form-label small text-muted">USD ที่บันทึกจริง</label>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className="form-control"
                                    placeholder="เช่น 21.00"
                                    value={line.usd}
                                    onChange={(e) => setUsd(line.product_code, e.target.value)}
                                  />
                                  <div className="small text-muted mt-1">
                                    ราคารวมที่ลูกค้าจ่าย
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          <div className="small text-muted mb-1">Allocation preview (เก่าสุดก่อน)</div>
                          <div className="table-responsive">
                            <table className="table table-sm mb-0 align-middle">
                              <thead>
                                <tr>
                                  <th>Bill</th>
                                  <th className="text-end">Qty</th>
                                  <th className="text-end">USD</th>
                                  <th className="text-end">Cost/Unit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allocations.map((alloc) => (
                                  <tr key={`${alloc.bill_no}-${alloc.order_item_id}`}>
                                    <td>
                                      <div className="fw-semibold">{alloc.bill_no}</div>
                                      <div className="small text-muted">order_item #{alloc.order_item_id}</div>
                                    </td>
                                    <td className="text-end">{alloc.qty}</td>
                                    <td className="text-end">{formatCurrency(alloc.usd, "USD")}</td>
                                    <td className="text-end">{formatCurrency(alloc.unit_cost)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 mt-3">
              <div className="card-body p-3">
                <div className="fw-semibold mb-2">สินค้าที่มี USA PENDING remaining</div>
                {allocationSources.length === 0 ? (
                  <div className="small text-muted">ไม่มีสินค้าที่ขายได้ในบิล USA PENDING</div>
                ) : (
                  <div className="table-responsive pe-2" style={{ maxHeight: 260 }}>
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Bill เก่าสุด</th>
                          <th className="text-end">ราคา USA</th>
                          <th className="text-end pe-3" style={{ width: 96 }}>
                            Remaining
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(remainingByCode.entries()).map(([code, remaining]) => {
                          const first = allocationSources.find((row) => row.product_code === code);
                          const usaPrice = usaPriceByCode.get(code) ?? null;
                          return (
                            <tr key={code}>
                              <td>
                                <div className="fw-semibold">{code}</div>
                                <div className="small text-muted">{productNameByCode.get(code) ?? ""}</div>
                              </td>
                              <td>
                                <div>{first?.bill_no ?? "-"}</div>
                                <div className="small text-muted">{formatDate(first?.created_at ?? null)}</div>
                              </td>
                              <td className={`text-end fw-semibold ${usaPrice == null ? "text-warning" : ""}`}>
                                {formatUSAPrice(usaPrice)}
                              </td>
                              <td className="text-end pe-3">
                                <span className="badge rounded-pill bg-warning text-dark border border-warning-subtle px-2">
                                  {remaining}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
